import { createContext, type ComponentChildren } from "preact";
import { useCallback, useContext, useEffect, useMemo, useState } from "preact/hooks";
import { defineMessages, intlLocale, useLocale, useT } from "../i18n/index.ts";
import {
  alreadyIn,
  baseLanguage,
  cached,
  type ContentChoice,
  type ContentMode,
  flagOf,
  inlineMarkdown,
  loadChoice,
  offersToggle,
  saveChoice,
  type TranslateMany,
  translateMissing,
} from "../contentLanguage.ts";

const messages = defineMessages(
  {
    toggle: "Langue de la discussion",
    inBook: "Dans la langue du livre ({language})",
    inInterface: "En {language}",
    question: "Dans quelle langue voulez-vous les réponses et les quiz ?",
    remember: "Retenir ce choix pour ce livre",
  },
  {
    toggle: "Language of the conversation",
    inBook: "In the language of the book ({language})",
    inInterface: "In {language}",
    question: "In which language do you want the answers and the quizzes?",
    remember: "Remember this choice for this book",
  },
);

type ContentLanguage = {
  book: string;
  target: string;
  /** The language chosen for the book: the one a new conversation or quiz starts in. */
  mode: ContentMode;
  /** Not asked yet for this book: the question is shown at the top of the chat and of the quiz. */
  unanswered: boolean;
  /** Not asked yet, or not read on the device yet. */
  undecided: boolean;
  choose: (choice: ContentChoice) => void;
  translate: TranslateMany;
};

const ContentLanguageContext = createContext<ContentLanguage | null>(null);

/**
 * Repris de Studio (nextjs/src/reader/components/ContentLanguage.tsx). The language of the conversations and quizzes on a book in another language than the interface (see
 * contentLanguage.ts). Nothing is offered when the book is in the language of the interface, or its language is unknown.
 */
export function ContentLanguageProvider({
  bookKey,
  bookLanguage,
  translate,
  children,
}: {
  /** The book, to keep the choice of the reader per book. */
  bookKey: string;
  bookLanguage: string | null | undefined;
  translate: TranslateMany;
  children: ComponentChildren;
}) {
  const locale = useLocale();
  const offered = offersToggle(bookLanguage, locale);
  // Read on the device once mounted (undefined until then: nothing asked before it is known).
  const [choice, setChoice] = useState<ContentChoice | null | undefined>(undefined);
  useEffect(() => setChoice(loadChoice(bookKey)), [bookKey]);
  const choose = useCallback(
    (next: ContentChoice) => {
      setChoice(next);
      saveChoice(bookKey, next);
    },
    [bookKey],
  );
  const mode = choice?.mode ?? "book";
  const unanswered = choice === null;
  const undecided = choice == null;
  const value = useMemo(
    () => (offered ? { book: baseLanguage(bookLanguage), target: locale, mode, unanswered, undecided, choose, translate } : null),
    [offered, bookLanguage, locale, mode, unanswered, undecided, choose, translate],
  );
  return <ContentLanguageContext.Provider value={value}>{children}</ContentLanguageContext.Provider>;
}

/** The two languages offered (the book's, the interface's), or null when the book is in the language of the interface. */
export function useLanguages(): { book: string; target: string } | null {
  const context = useContext(ContentLanguageContext);
  return context ? { book: context.book, target: context.target } : null;
}

/** The language a new conversation or quiz starts in (the one chosen for the book); null when nothing is offered. */
export function useDefaultLanguage(): string | null {
  const context = useContext(ContentLanguageContext);
  if (!context) return null;
  return context.mode === "interface" ? context.target : context.book;
}

/** The language of a book in another language is not chosen yet: a quiz waits for it before it starts on its own. */
export function useLanguageUndecided(): boolean {
  return !!useContext(ContentLanguageContext)?.undecided;
}

/**
 * The texts shown in the language of their conversation: as written, or translated into it (each in place once its
 * translation arrives; until then, and when it fails, the text as written). `into`: null for a conversation that has
 * no language (begun before it was chosen), whose texts are shown as written.
 */
export function useContentTexts(texts: string[], into: string | null | undefined): string[] {
  const context = useContext(ContentLanguageContext);
  const target = into ? baseLanguage(into) : "";
  const translating = !!context && !!target;
  const toTranslate = useMemo(
    () => (translating ? texts.filter((text) => text.trim() && !alreadyIn(text, target)) : []),
    // The texts, not their array: a new array at each render of the host.
    [translating, target, texts.join("\u0001")],
  );
  const [, setArrived] = useState(0);
  useEffect(() => {
    if (!context || !toTranslate.length || toTranslate.every((text) => cached(text, target) !== undefined)) return;
    let live = true;
    // The language of an answer written by the pipeline is not known: the server finds it.
    void translateMissing(toTranslate, "", target, context.translate).then(() => live && setArrived((count) => count + 1));
    return () => {
      live = false;
    };
  }, [toTranslate, target]);
  return translating ? texts.map((text) => cached(text, target) ?? text) : texts;
}

/** A short text of the quiz with its inline Markdown (bold, italic). */
export function InlineText({ text }: { text: string }) {
  return (
    <>
      {inlineMarkdown(text).map((part, index) =>
        part.strong ? <strong key={index}>{part.text}</strong> : part.em ? <em key={index}>{part.text}</em> : part.code ? <code key={index}>{part.text}</code> : part.text,
      )}
    </>
  );
}

function useLanguageNames(capitalized = false) {
  const locale = useLocale();
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([intlLocale(locale)], { type: "language" });
    } catch {
      return null;
    }
  }, [locale]);
  return (code: string) => {
    const name = names?.of(code) ?? code;
    if (!capitalized) return name;
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  };
}

/**
 * At the first chat or quiz on a book in another language (Stéphane, 19/10/2026): in which language the conversations
 * and the quizzes start, and whether to keep that choice for this book.
 */
export function LanguageQuestion() {
  const t = useT(messages);
  const context = useContext(ContentLanguageContext);
  const name = useLanguageNames(true);
  const [remember, setRemember] = useState(true);
  if (!context?.unanswered) return null;
  const options: Array<{ mode: ContentMode; language: string }> = [
    { mode: "book", language: context.book },
    { mode: "interface", language: context.target },
  ];
  return (
    <div class="msb-language-question" role="group" aria-label={t("question")}>
      <p>{t("question")}</p>
      <div class="msb-language-question__choices">
        {options.map((option) => (
          <button key={option.mode} type="button" onClick={() => context.choose({ mode: option.mode, remembered: remember })}>
            <span aria-hidden="true">{flagOf(option.language) ?? option.language.toUpperCase()}</span>
            {name(option.language)}
          </button>
        ))}
      </div>
      <label>
        <input type="checkbox" checked={remember} onChange={(event) => setRemember((event.currentTarget as HTMLInputElement).checked)} />
        {t("remember")}
      </label>
    </div>
  );
}

/**
 * The two flags in the area of the question of a new conversation or quiz (Stéphane, 19/10/2026): its language, on
 * the one chosen for the book until changed for this one; fixed once it is started (the flags are gone then).
 */
export function LanguagePicker({ value, onChange, disabled }: { value: string; onChange: (language: string) => void; disabled?: boolean }) {
  const t = useT(messages);
  const context = useContext(ContentLanguageContext);
  const name = useLanguageNames();
  if (!context) return null;
  const options = [
    { language: context.book, label: t("inBook", { language: name(context.book) }) },
    { language: context.target, label: t("inInterface", { language: name(context.target) }) },
  ];
  return (
    <div class="msb-language-toggle" role="group" aria-label={t("toggle")}>
      {options.map((option) => (
        <button
          key={option.language}
          type="button"
          aria-pressed={baseLanguage(value) === option.language}
          aria-label={option.label}
          title={option.label}
          disabled={disabled}
          onClick={() => onChange(option.language)}
        >
          {flagOf(option.language) ?? <span class="msb-language-toggle__code">{option.language.toUpperCase()}</span>}
        </button>
      ))}
    </div>
  );
}
